// Add an existing device to this account, by Unique Number or share code.
//
// Serves BOTH entry points that previously pointed at nothing:
//   /add-device   "Add Device"        — type the number printed on the device
//   /scan-share   "Add Shared Device" — paste/type another user's share code
//
// The two differ only in wording; claimDevice() parses either form, so a share
// code pasted into the plain field works and vice versa.
//
// ⚠️ NOT a camera scanner. Reading a QR needs a native scanning plugin, which
// this build doesn't carry — adding one is a native change and therefore a Play
// release, so it is deliberately left out rather than half-built. The share code
// is short and can be typed or pasted, which reaches the same endpoint with the
// same result. See docs/08-flutter-parity-plan.md.

import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import { claimDevice } from '../../../core/devices/claimDevice';

export default function AddDeviceScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userId } = useApp();

  const shared = pathname.startsWith('/scan-share');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      setResult(await claimDevice(userId, code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title={shared ? 'Add Shared Device' : 'Add Device'} showBack />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          {result ? (
            <Box sx={{ textAlign: 'center', pt: 4 }}>
              {result.ok
                ? <CheckCircleIcon sx={{ fontSize: 72, color: '#4CAF50' }} />
                : <ErrorOutlineIcon sx={{ fontSize: 72, color: '#F44336' }} />}
              <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 2 }}>
                {result.title}
              </Typography>
              <Typography sx={{ color: `${c.text}B3`, mt: 1.5 }}>{result.message}</Typography>
              <Button
                onClick={() => (result.ok ? navigate('/home?tab=1') : setResult(null))}
                sx={{
                  mt: 4, bgcolor: c.button, color: c.text, px: 3, py: 1.25,
                  borderRadius: '12px', textTransform: 'none',
                  '&:hover': { bgcolor: c.button },
                }}
              >
                {result.ok ? 'Done' : 'Try Again'}
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ color: `${c.text}B3`, fontSize: 14, mb: 2 }}>
                {shared
                  ? "Enter the share code from the device owner. It looks like "
                    + '"u%#12n%#5585891".'
                  : 'Enter the Unique Number printed on the device. It must already '
                    + 'be powered on and connected to Wi-Fi.'}
              </Typography>

              <TextField
                fullWidth
                autoFocus
                label={shared ? 'Share code' : 'Unique Number'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                sx={{
                  '& .MuiInputLabel-root': { color: `${c.text}B3` },
                  '& .MuiOutlinedInput-root': {
                    color: c.text,
                    borderRadius: '12px',
                    '& fieldset': { borderColor: `${c.text}66` },
                    '&:hover fieldset': { borderColor: c.text },
                    '&.Mui-focused fieldset': { borderColor: c.text },
                  },
                }}
              />

              <Button
                fullWidth
                onClick={submit}
                disabled={busy || !code.trim()}
                sx={{
                  mt: 3, bgcolor: c.button, color: c.text, py: 1.5,
                  borderRadius: '12px', fontSize: 16, textTransform: 'none',
                  '&:hover': { bgcolor: c.button },
                }}
              >
                {busy ? <CircularProgress size={20} sx={{ color: c.text }} /> : 'Add Device'}
              </Button>

              {!shared && (
                <Typography sx={{ color: `${c.text}99`, fontSize: 13, mt: 3 }}>
                  Setting up a brand-new device? Use “Set Up New Device” first to
                  send it your Wi-Fi.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
